import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { IntlProvider } from "react-intl-next";

import {
    JiraModalBackground,
    PreviewContainer,
    type Initials
} from "@atlassian/product-setup";

import { getFirstName, getLastName } from "@/components/signup/utils";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { StepContext, StepProps } from "../../../types";

import { ArrowSvg } from "./arrow-svg";

const previewStyles = css({
    "position": "relative",
    "@media (max-width: 1023px)": {
        display: "none"
    }
});

const previewCardStyles = css({
    position: "relative",
    marginInlineStart: "55px",
    paddingInlineEnd: "1px",
    width: "109%",
    height: "32pc"
});

const modalBackgroundStyles = css({
    position: "absolute",
    insetBlockStart: "-64px"
});

/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors -- consumer-side override of @atlassian/product-setup preview styles */
const previewTableAiOnboardingOverrides = css({
    "& th:nth-child(1) > div": { minWidth: "40px" }
});

const previewTableOverrides = css({
    "& > div:last-child": {
        overflowY: "auto"
    },
    "& section > div": {
        gap: "16px"
    },
    "& table": {
        tableLayout: "fixed",
        width: "100%"
    },
    "& th:nth-child(1)": { width: "8%" },
    "& th:nth-child(2)": { width: "92%" },
    "& td": {
        fontSize: "14px",
        fontWeight: "400",
        fontStyle: "normal"
    }
});
/* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors */

export const Preview = <T extends StepContext>({
    context,
    screenId
}: StepProps<T>) => {
    const { locale } = useIntl();
    const loggedInUser = useLoggedInUser();
    const shouldShowArrow = screenId === "space-details";
    const shouldShowCollaborationCursors =
        screenId === "invite" || screenId === "loading";
    let userInitials = "";
    if (loggedInUser.name) {
        const firstInital = getFirstName(loggedInUser.name).charAt(0);
        const lastInitial = getLastName(loggedInUser.name).charAt(0);
        userInitials = firstInital + lastInitial;
    }

    return (
        <IntlProvider locale={locale}>
            <div css={previewStyles}>
                <div css={modalBackgroundStyles}>
                    <JiraModalBackground />
                </div>
                <div
                    css={[
                        previewCardStyles,
                        previewTableAiOnboardingOverrides,
                        context.previewCardData && previewTableOverrides
                    ]}
                >
                    {shouldShowArrow && (
                        <div
                            css={css({
                                position: "absolute",
                                insetInlineEnd: "50px",
                                insetBlockStart: "-80px"
                            })}
                        >
                            <ArrowSvg />
                        </div>
                    )}
                    <PreviewContainer
                        spaceName={context.spaceName ?? ""}
                        userInitials={userInitials as Initials}
                        previewCardData={
                            context.previewCardData ?? {
                                previewType: context.template
                            }
                        }
                        shouldShowCollaborationCursors={
                            shouldShowCollaborationCursors
                        }
                    />
                </div>
            </div>
        </IntlProvider>
    );
};
